import Modal from '@components/v2/ui/Modal/Modal';
import FileCopyRoundedIcon from '@mui/icons-material/FileCopyRounded';
import MailIcon from '@mui/icons-material/Mail';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, Stack, Tab, Tabs } from '@mui/material';
import LangEnum from '../../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../translation/TranslationContext';
import OrderPaymentCopyLinkContent from './components/OrderPaymentCopyLinkContent';
import OrderPaymentEmailContent from './components/OrderPaymentEmailContent';
import OrderPaymentWhatsappContent from './components/OrderPaymentWhatsappContent';
import { ShareLinkPanel } from './enums/ShareLinkPanel';
import useOrderSharePaymentModal from './hooks/useOrderSharePaymentModal';

interface OrderSharePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  orderCode: string;
  onClickBack: () => void;
  paymentUrl: string;
  onTransitionExited: () => void;
  customerId?: string;
}

const tabs = [
  {
    panel: ShareLinkPanel.WHATSAPP,
    label: LangEnum.GLOBAL_WHATSAPP,
    icon: <WhatsAppIcon fontSize="small" />,
  },
  {
    panel: ShareLinkPanel.EMAIL,
    label: LangEnum.GLOBAL_EMAIL,
    icon: <MailIcon fontSize="small" />,
  },
  {
    panel: ShareLinkPanel.COPY_LINK,
    label: LangEnum.GLOBAL_LINK,
    icon: <FileCopyRoundedIcon fontSize="small" />,
  },
];

const OrderSharePaymentModal = ({
  isOpen,
  onClose,
  onClickBack,
  orderCode,
  paymentUrl,
  orderId,
  customerId,
  onTransitionExited,
}: OrderSharePaymentModalProps) => {
  const { translate } = useTranslation();

  const {
    emails,
    currentPanel,
    disabled,
    whatsappMessage,
    onSubmit,
    onChangePanel,
    onChangeEmails,
    onChangeWhatsappMessage,
  } = useOrderSharePaymentModal({ orderCode, paymentUrl, orderId });

  return (
    <Modal
      isOpen={isOpen}
      size="standard"
      header={{ title: `${translate(LangEnum.ORDER_PAYMENT_TITLE)} ${orderCode}` }}
      onClose={onClose}
      onExited={onTransitionExited}
      footer={{
        primaryAction: {
          label: translate(currentPanel.label),
          onClick: onSubmit,
          isDisabled: disabled,
        },
        secondaryAction: {
          label: translate(LangEnum.GLOBAL_GO_BACK),
          onClick: onClickBack,
        },
      }}
      slots={{
        headerBottom: (
          <Tabs
            variant="fullWidth"
            value={currentPanel.panel}
            indicatorColor="primary"
            onChange={onChangePanel}
            sx={{ minHeight: '40px' }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.panel}
                iconPosition="start"
                icon={tab.icon}
                label={translate(tab.label)}
                sx={{ minHeight: '40px' }}
                value={tab.panel}
              />
            ))}
          </Tabs>
        ),
      }}
    >
      <Stack
        spacing={1}
        sx={{
          minHeight: 235,
        }}
      >
        <Box
          sx={{
            paddingY: 1,
          }}
        >
          {currentPanel.panel === ShareLinkPanel.WHATSAPP && (
            <OrderPaymentWhatsappContent
              message={whatsappMessage}
              onChangeMessage={onChangeWhatsappMessage}
            />
          )}
          {currentPanel.panel === ShareLinkPanel.EMAIL && (
            <OrderPaymentEmailContent
              emails={emails}
              onChangeEmails={onChangeEmails}
              customerId={customerId}
            />
          )}
          {currentPanel.panel === ShareLinkPanel.COPY_LINK && (
            <OrderPaymentCopyLinkContent paymentUrl={paymentUrl} />
          )}
        </Box>
      </Stack>
    </Modal>
  );
};

export default OrderSharePaymentModal;
